import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Input, Modal, Select } from "antd";
import {
  createDriverMutation,
  getDriversQueryKey,
} from "../../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { extractErrorMessage } from "../../../util/extractErrorMessage";

type CreateDriverModalProps = {
  open: boolean;
  onClose: () => void;
  site_id: string;
  partner_id: string;
};

type FormValues = {
  email: string;
  badge_ids?: string[];
};

export const CreateDriverModal: React.FC<CreateDriverModalProps> = ({
  open,
  onClose,
  site_id,
  partner_id,
}) => {
  const [form] = Form.useForm<FormValues>();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    ...createDriverMutation(),
    onSuccess: (driver) => {
      queryClient.invalidateQueries({
        queryKey: getDriversQueryKey({ query: { site_ids: [site_id] } }),
      });
      notify({ type: "success", message: `Driver ${driver.email} created` });
      onClose();
    },
    onError: (error) => {
      notify({
        type: "error",
        message: "Failed to create driver",
        description: extractErrorMessage(error),
      });
    },
  });

  const handleSubmit = (values: FormValues) => {
    createMutation.mutate({
      body: {
        email: values.email.trim(),
        badge_ids: (values.badge_ids ?? [])
          .map((badgeId) => badgeId.trim())
          .filter(Boolean),
        partner_id,
        site_ids: [site_id],
      },
    });
  };

  return (
    <Modal
      title="Add driver"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Add driver"
      confirmLoading={createMutation.isPending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        preserve={false}
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input placeholder="driver@example.com" />
        </Form.Item>
        <Form.Item
          label="Badge IDs"
          name="badge_ids"
          extra="Optional. Press enter or comma after each badge ID."
        >
          <Select
            mode="tags"
            tokenSeparators={[","]}
            open={false}
            placeholder="badge-001, badge-002"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
